import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

/**
 * Password policy mirroring the backend PasswordPolicyValidator.
 *
 * Backend rules enforced client-side here:
 *   - blank / whitespace-only is rejected
 *   - no control characters (0x00-0x1F or 0x7F)
 *   - minimum 12 unicode code points
 *   - maximum 72 UTF-8 bytes
 *   - at least one digit
 *   - at least one special character (printable ASCII non-alphanumeric, excluding space)
 *
 * Note: the "common passwords top 1000" rule is enforced by the backend only.
 * The server surfaces a 400 with a human-readable message when violated.
 */

export const PASSWORD_MIN_CODE_POINTS = 12;
export const PASSWORD_MAX_BYTES = 72;

export const PASSWORD_SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=\[\]{}|;:'",.<>/?]/;
export const PASSWORD_DIGIT_REGEX = /[0-9]/;
export const PASSWORD_CONTROL_CHAR_REGEX = /[\x00-\x1F\x7F]/;

export interface PasswordRequirementResult {
  minLength: boolean;
  maxBytes: boolean;
  hasDigit: boolean;
  hasSpecialChar: boolean;
  noControlChar: boolean;
  notBlank: boolean;
}

export interface PasswordRequirements extends PasswordRequirementResult {
  allMet: boolean;
}

function codePointCount(value: string): number {
  return [...value].length;
}

let cachedEncoder: TextEncoder | null = null;

function utf8ByteLength(value: string): number {
  if (typeof TextEncoder !== 'undefined') {
    cachedEncoder ??= new TextEncoder();
    return cachedEncoder.encode(value).length;
  }
  // Fallback for environments without TextEncoder (e.g. older jsdom in unit tests).
  return unescape(encodeURIComponent(value)).length;
}

export function evaluatePasswordRequirements(
  rawPassword: string | null | undefined
): PasswordRequirements {
  const value = rawPassword ?? '';
  const trimmed = value.trim();

  const notBlank = value.length > 0 && trimmed.length > 0;
  const noControlChar = !PASSWORD_CONTROL_CHAR_REGEX.test(value);
  const minLength = codePointCount(value) >= PASSWORD_MIN_CODE_POINTS;
  const maxBytes = utf8ByteLength(value) <= PASSWORD_MAX_BYTES;
  const hasDigit = PASSWORD_DIGIT_REGEX.test(value);
  const hasSpecialChar = PASSWORD_SPECIAL_CHAR_REGEX.test(value);

  const allMet = notBlank && noControlChar && minLength && maxBytes && hasDigit && hasSpecialChar;

  return { notBlank, noControlChar, minLength, maxBytes, hasDigit, hasSpecialChar, allMet };
}

/**
 * Reactive forms validator for the password policy. Error keys match the backend
 * names so messages can be reused.
 */
export const passwordPolicyValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value: string = control.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = evaluatePasswordRequirements(value);

  const errors: ValidationErrors = {};
  if (!result.notBlank) errors.passwordBlank = true;
  if (!result.noControlChar) errors.passwordIllegalCharacter = true;
  if (!result.minLength) errors.passwordTooShort = true;
  if (!result.maxBytes) errors.passwordTooLong = true;
  if (!result.hasDigit) errors.passwordMissingDigit = true;
  if (!result.hasSpecialChar) errors.passwordMissingSpecialChar = true;

  return Object.keys(errors).length ? errors : null;
};

/**
 * Standard set of validators for any "new password" field. Use with FormBuilder:
 *
 *   password: ['', PASSWORD_VALIDATORS]
 */
export const PASSWORD_VALIDATORS: ValidatorFn[] = [Validators.required, passwordPolicyValidator];

export const PASSWORD_REQUIREMENTS_COPY: { key: keyof PasswordRequirementResult; label: string }[] = [
  { key: 'minLength', label: `Be at least ${PASSWORD_MIN_CODE_POINTS} characters` },
  { key: 'hasDigit', label: 'Contain a number' },
  { key: 'hasSpecialChar', label: 'Contain a special character (e.g. !@#$%^&*)' }
];
